import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';

// Services
import {
  getDefaultOptionDrop,
  getTranslateByFullKey,
  getTranslateByKey,
  isNullUndefined,
  removeEmptyValues
} from '@/main/services/utils';

// Models
import { KeyValue } from '@/main/models/common';

// Reducer
import { getPartType, usePartTypeReducer } from '@/main/store/partType';
import { useDispatch } from 'react-redux';
import { getCategory, useCategoryReducer } from '@/main/store/category';
import {
  getSubcategory,
  resetReducerSubcategory,
  useSubcategoryReducer
} from '@/main/store/subcategory';
import {
  getPosition,
  resetReducerPosition,
  usePositionReducer
} from '@/main/store/position';

// Api
import { partTypePerPartApi } from '@/main/api';

// Components
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import useAsyncModalConfirmReason from './../asyncModalConfirmReason/asyncModalConfirmReason';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';
import { toBase64 } from '@/main/services/utilsFile';

// Styles and local components
import SecondaryButton from '../button/secondaryButton';
import * as S from './styles';
import { PartTypePerPartResponse } from '@/main/models/response/partTypePerPartResponse';
import { PartTypePerPartRequest } from '@/main/models/request/PartTypePerPartRequest';
import { Guid } from 'guid-typescript';
import SelectXnuupPartIdAutoCompleteComponent from '../xnuupPartIdAutoComplete/selectXnuupPartIdAutoCompleteComponent';
import { XnuupPartIdAutoCompleteResponse } from '@/main/models/response/xnuupPartIdResponse';
import { PositionRequest } from '@/main/models/request';
import { SubcategoryRequest } from '@/main/models/request/subcategoryRequest';
import { useDinamicFiltersMappingCodes } from '@/main/screens/integration/mappingCodes/dinamicFiltersMappingCodes/dinamicFiltersMappingCodes';
import { KeyValueId } from '@/main/models/common/keyValueId';

interface IPartTypePerPartModal {
  dataPartTypePerPart: PartTypePerPartResponse | null;
  guidBodyShop?: string | null;
  guidInsurer?: string | null;
  setPartTypePerPartGrid: React.Dispatch<
    React.SetStateAction<PartTypePerPartResponse[]>
  >;
  closeModal: (()=> void);
}
function PartTypePerPartModal({
  dataPartTypePerPart,
  guidBodyShop = null,
  guidInsurer = null,
  setPartTypePerPartGrid,
  closeModal
}: IPartTypePerPartModal) {
  const [loadingSave, setLoadingSave] = useState<boolean>(false);
  const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();

  const dispatch = useDispatch();

  const categoryReducer = useCategoryReducer();
  const subcategoryReducer = useSubcategoryReducer();
  const positionReducer = usePositionReducer();
  const partTypeReducer = usePartTypeReducer();
  const optionDefaultSubcategoryDrop = getDefaultOptionDrop(
    subcategoryReducer.subcategory.data
  );

  const INITIAL_FORM_STATE_SAVE = {
    partCategory: null as KeyValue | null,
    partSubcategory: null as KeyValue | null,
    partPosition: null as KeyValue | null,
    partName: '' as string | null,
    part: null as KeyValue | null,
    partType: null as KeyValue | null,
    xnuupPartId: null as KeyValueId | null
  };

  const {
    control,
    setValue,
    reset,
    register,
    handleSubmit,
    clearErrors,
    resetField,
    formState: { errors }
  } = useForm<IPartTypePerPartFormSave>({
    mode: 'all',
    defaultValues: INITIAL_FORM_STATE_SAVE
  });

  const watcherCategory = useWatch({
    name: 'partCategory',
    control
  });

  const watcherSubcategory = useWatch({
    name: 'partSubcategory',
    control
  });

  const watcherXnuupPartId = useWatch({
    name: 'part',
    control
  });

  const [hasClear, setHasClear] = useState<boolean>(false);

  const isDisabledSubcategoryDrop = () =>
    subcategoryReducer.subcategory.data === null || watcherCategory === null;

  const isDisabledPositionDrop = () =>
    positionReducer.position.data === null || watcherXnuupPartId === null;

  type IPartTypePerPartFormSave = typeof INITIAL_FORM_STATE_SAVE;
  const { keyReducer } = useDinamicFiltersMappingCodes();

  async function onSubmitSave(data: IPartTypePerPartFormSave) {
    let resultConfirm = null;
    let filesRequest = undefined;

    if (guidInsurer || guidBodyShop) {
      resultConfirm = await getConfirmationReason(
        getTranslateByKey('information.confirmChangeTitle'),
        ReasonTypeEnum.ChangeCompany
      );

      if (!resultConfirm.confirmed) {
        setLoadingSave(false);
        return;
      }

      if (resultConfirm.files && resultConfirm.files.length > 0) {
        filesRequest = await Promise.all(
          resultConfirm.files.map(async item => {
            const base64DataUrl = await toBase64(item);
            return {
              fileName: item.name,
              contentType: item.type,
              fileBase64: base64DataUrl as string
            };
          })
        );
      }
    }

    setLoadingSave(true);

    const partTypePerPartRequest = removeEmptyValues({
      guidPartType: data.partType?.key.toString(),
      guidInsurer: guidInsurer,
      guidBodyShop: guidBodyShop,
      guidCategory: data.partCategory?.key,
      guidSubcategory: data.partSubcategory?.key,
      guidPart: data.part?.key,
      guidPosition: data.partPosition?.key
    }) as PartTypePerPartRequest;

    if (guidInsurer || guidBodyShop) {
      partTypePerPartRequest.changeJustification = {
        guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
        reasonDescription: resultConfirm?.observation || null,
        filesUpload: filesRequest
      };
    }

    partTypePerPartApi
      .savePartTypePerPart(partTypePerPartRequest)
      .then(res => {
        const resData = res.data.result;
        if (!isNullUndefined(resData)) {
          setPartTypePerPartGrid(prevState => {
            return [...prevState, resData];
          });
        }
        toast.success(
          getTranslateByKey('information.partTypePerPartSavedSuccessfully')
        );
        reset(INITIAL_FORM_STATE_SAVE);
        setHasClear(true);
        closeModal();
      })
      .finally(() => setLoadingSave(false));
  }

  function loadOptionsByXnuupPartId(partId: XnuupPartIdAutoCompleteResponse) {
    if (!partId) return resetField('part');
    setValue('partName', partId.partPIESName);

    const {
      partCategoryGuid,
      partCategoryName,
      partGuid,
      partPIESName,
      partSubCategoryGuid,
      partSubCategoryName,
      piesPartTerminologyId
    } = partId;

    if (partCategoryGuid && partCategoryName) {
      clearErrors();
      setValue('partCategory', {
        key: partCategoryGuid,
        value: partCategoryName
      });
      if (isNullUndefined(categoryReducer.category.data))
        dispatch(getCategory());

      if (partSubCategoryGuid && partSubCategoryName) {
        setValue('partSubcategory', {
          key: partSubCategoryGuid,
          value: partSubCategoryName
        });
        const request: SubcategoryRequest = {
          guid: String(partCategoryGuid),
          isCanastaBasica: false
        };
        dispatch(getSubcategory(request, keyReducer));

        if (partGuid) {
          const request: PositionRequest = {
            partGuid: String(partGuid)
          };
          dispatch(getPosition(request, keyReducer));
        } else {
          dispatch(resetReducerPosition(keyReducer));
          setValue('partPosition', null);
        }
      } else {
        dispatch(resetReducerSubcategory(keyReducer));
        setValue('partSubcategory', null);
      }
    } else {
      setValue('partCategory', null);
    }

    setValue('part', {
      id: piesPartTerminologyId,
      key: partGuid,
      value: partPIESName
    } as KeyValue);
  }

  useEffect(() => {
    dispatch(getCategory());
    dispatch(getPartType());
  }, []);

  useEffect(() => {
    if (dataPartTypePerPart) {
      if (
        isNullUndefined(dataPartTypePerPart.guidCategory) ||
        dataPartTypePerPart.guidCategory === Guid.EMPTY
      ) {
        setValue('partCategory', null);
      } else {
        setValue('partCategory', {
          key: dataPartTypePerPart.guidCategory,
          value: dataPartTypePerPart.category
        });
        callSubcategoryApiDomain({
          key: dataPartTypePerPart.guidCategory,
          value: dataPartTypePerPart.category
        });
      }
      if (
        isNullUndefined(dataPartTypePerPart.guidSubcategory) ||
        dataPartTypePerPart.guidSubcategory === Guid.EMPTY
      ) {
        setValue('partSubcategory', null);
      } else {
        setValue('partSubcategory', {
          key: dataPartTypePerPart.guidSubcategory,
          value: dataPartTypePerPart.subCategory
        });
      }
      if (
        isNullUndefined(dataPartTypePerPart.guidPosition) ||
        dataPartTypePerPart.guidPosition === Guid.EMPTY
      ) {
        setValue('partPosition', null);
      } else {
        setValue('partPosition', {
          key: dataPartTypePerPart.guidPosition,
          value: dataPartTypePerPart.position
        });
      }
      if (
        isNullUndefined(dataPartTypePerPart.guidSubcategory) ||
        (!dataPartTypePerPart.xnuupId &&
          !dataPartTypePerPart.partPIESName &&
          dataPartTypePerPart.partGuid === Guid.EMPTY)
      ) {
        setValue('xnuupPartId', null);
      } else {
        setValue('xnuupPartId', {
          id: dataPartTypePerPart.xnuupId,
          key: dataPartTypePerPart.partGuid,
          value: dataPartTypePerPart.partPIESName
        });
      }

      if (
        dataPartTypePerPart.partGuid !== Guid.EMPTY &&
        !isNullUndefined(dataPartTypePerPart.partGuid)
      ) {
        const request: PositionRequest = {
          partGuid: String(dataPartTypePerPart.partGuid)
        };
        dispatch(getPosition(request));
      }
    }
  }, [JSON.stringify(dataPartTypePerPart)]);

  function callSubcategoryApiDomain(optionSelected: KeyValue | null) {
    setValue('partSubcategory', null);
    setValue('partPosition', null);
    dispatch(resetReducerSubcategory());
    dispatch(resetReducerPosition());

    if (optionSelected !== null && optionSelected.key) {
      const request: SubcategoryRequest = {
        guid: String(optionSelected.key),
        isCanastaBasica: false
      };
      dispatch(getSubcategory(request));
    }
  }

  return (
    <>
      <S.Title>
        {getTranslateByFullKey('t.nah.information.registerPartTypePerPart')}
      </S.Title>
      <form onSubmit={handleSubmit(onSubmitSave)} noValidate>
        <S.Container>
          <KogAutoCompleteControlled
            id="id-partTypePerPartModal-select-category"
            nameCustom="partCategory"
            label={getTranslateByFullKey('t.nah.quoteCard.category')}
            isDefaultValueUnique
            control={control}
            rulesCustom={{ required: true }}
            setValueCustom={setValue}
            options={categoryReducer.category.data}
            isLoading={categoryReducer.category.isLoading}
            onChangeCustom={(optionSelected: KeyValue | null) => {
              callSubcategoryApiDomain(optionSelected);
            }}
          />
          <KogAutoCompleteControlled
            id="id-partTypePerPartModal-select-subcategory"
            nameCustom="partSubcategory"
            label={getTranslateByFullKey('t.nah.quoteCard.subCategory')}
            isDefaultValueUnique
            control={control}
            rulesCustom={{ required: true }}
            errorMessageCustom={
              errors.partSubcategory?.type === 'required'
                ? getTranslateByKey('information.subcategoryRequired')
                : ''
            }
            hasErrorCustom={!!errors.partSubcategory}
            setValueCustom={setValue}
            options={subcategoryReducer.subcategory.data}
            isLoading={subcategoryReducer.subcategory.isLoading}
            onChangeCustom={() => {}}
            disabled={
              isDisabledSubcategoryDrop() || !!optionDefaultSubcategoryDrop
            }
          />

          <SelectXnuupPartIdAutoCompleteComponent
            watcherCategory={watcherCategory}
            watcherSubcategory={watcherSubcategory}
            hasClearValue={hasClear}
            setHasClearValue={setHasClear}
            partPIESName={null}
            isCanastaBasica={false}
            handleChangeXnuupPartId={(
              partId: XnuupPartIdAutoCompleteResponse
            ) => {
              loadOptionsByXnuupPartId(partId);
            }}
            errorMessageCustom={
              errors.part?.type === 'required'
                ? getTranslateByKey('information.partNameRequired', true)
                : ''
            }
            hasErrorCustom={!!errors.part}
            label={getTranslateByFullKey('t.nah.quoteCard.partName')}
            {...register('part', { required: true })}
          />

          <KogAutoCompleteControlled
            id="id-partTypePerPartModal-select-position"
            nameCustom="partPosition"
            label={getTranslateByKey('information.position')}
            control={control}
            rulesCustom={{ required: true }}
            isDefaultValueUnique
            options={positionReducer.position.data || []}
            isLoading={positionReducer.position.isLoading}
            disabled={isDisabledPositionDrop()}
            setValueCustom={setValue}
            onChangeCustom={() => {}}
          />

          <KogAutoCompleteControlled
            id="id-partTypePerPartModal-select-partTypes"
            nameCustom="partType"
            label={getTranslateByKey('information.partTypes')}
            control={control}
            rulesCustom={{ required: true }}
            isDefaultValueUnique
            options={partTypeReducer.partType.data?.sort((a, b) => a.value.localeCompare(b.value)) || []}
            isLoading={partTypeReducer.partType.isLoading}
            setValueCustom={setValue}
            onChangeCustom={() => {}}
          />
        </S.Container>
        <S.AreaButton>
          <SecondaryButton
            onClick={() => {
              closeModal();
              reset(INITIAL_FORM_STATE_SAVE);
              setHasClear(true);
            }}>
            {getTranslateByKey('information.back')}
          </SecondaryButton>
          <SecondaryButton
            id="id-partTypePerTypeModal-button-save"
            type="submit"
            isLoading={loadingSave}>
            {getTranslateByKey('information.save')}
          </SecondaryButton>
        </S.AreaButton>
      </form>
      <ModalConfirmationReason closeByOverlay={false} />
    </>
  );
}

export default PartTypePerPartModal;
